import axios from "axios";

const api = axios.create({
  baseURL:         "/api",
  withCredentials: true,  // Required to send/receive httpOnly refresh token cookie
});

// ── Request interceptor: attach access token ──────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor: auto-refresh on 401 TOKEN_EXPIRED ──────────────
let isRefreshing  = false;
let failedQueue   = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    const isExpired =
      error.response?.status === 401 &&
      error.response?.data?.code === "TOKEN_EXPIRED" &&
      !original._retry;

    if (!isExpired) return Promise.reject(error);

    if (isRefreshing) {
      // Queue the request until refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing    = true;

    try {
      const { data } = await axios.post("/api/auth/refresh", {}, { withCredentials: true });
      const newToken = data.accessToken;
      localStorage.setItem("accessToken", newToken);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      processQueue(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      localStorage.removeItem("accessToken");
      window.location.href = "/login";
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;

// ── Auth ──────────────────────────────────────────────────────────────────
export const login          = (body)  => api.post("/auth/login", body);
export const logout         = ()      => api.post("/auth/logout");
export const getMe          = ()      => api.get("/auth/me");
export const refreshToken   = ()      => api.post("/auth/refresh");
export const changePassword = (body)  => api.patch("/auth/me/password", body);

// ── User Management (admin only) ──────────────────────────────────────────
export const getUsers       = ()      => api.get("/auth/users");
export const createUser     = (body)  => api.post("/auth/users", body);
export const updateUser     = (id, b) => api.patch(`/auth/users/${id}`, b);
export const deleteUser     = (id)    => api.delete(`/auth/users/${id}`);

// ── Filings ───────────────────────────────────────────────────────────────
export const uploadFiling = (file, onProgress) => {
  const form = new FormData();
  form.append("filing", file);
  return api.post("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / e.total)),
  });
};
export const submitEdgarUrl  = (url) => api.post("/upload/edgar-url", { url });
export const getFilingStatus = (id)  => api.get(`/upload/${id}`);
export const getFilings      = ()    => api.get("/filings");
export const deleteFiling    = (id)  => api.delete(`/filings/${id}`);

// ── Benchmark ─────────────────────────────────────────────────────────────
export const getModels       = ()    => api.get("/benchmark/models");
export const runBenchmark    = (d)   => api.post("/benchmark/run", d);
export const getBenchmarkRun = (id)  => api.get(`/benchmark/${id}`);
export const getFilingRuns   = (id)  => api.get(`/benchmark/filing/${id}`);
export const getPromptPreview = (id) => api.get(`/benchmark/preview/${id}`);

// ── Parse ─────────────────────────────────────────────────────────────────
export const parseWithModel  = (d)   => api.post("/parse", d);
export const getParseResult  = (id)  => api.get(`/parse/${id}`);

// ── Export ────────────────────────────────────────────────────────────────
export const getExportUrl    = (resultId, format) => `/api/export/${resultId}/${format}`;

// ── Validation ────────────────────────────────────────────────────────────
export const triggerValidation   = (id, body) => api.post(`/validate/${id}`, body);
export const getValidationReport = (id)       => api.get(`/validate/${id}`);
export const getReviewQueue      = ()         => api.get("/validate/review/queue");
export const getReviewForResult  = (id)       => api.get(`/validate/review/${id}`);
export const submitVerdicts      = (id, body) => api.patch(`/validate/review/${id}/verdicts`, body);